import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import type { SittingGroup } from '../lib/sitting';
import type { AnswerState } from './useSitting';

/**
 * The controls a candidate answers with, one per question type.
 *
 * Which control appears is decided by the group's `inputMode`, which the server
 * resolves — including the map- and diagram-labelling case, where the presence
 * of a letter bank decides between a text box and a dropdown. The player never
 * re-derives that from the type name, so the exam screen, the validator and the
 * scorer cannot disagree about what a question is.
 */

export interface AnswerProps {
  number: number;
  answer: AnswerState | undefined;
  disabled: boolean;
  onChange: (patch: Partial<AnswerState>) => void;
  onFocus: () => void;
}

/**
 * Renders a body of notes or a table, then puts a real input inside each gap.
 *
 * The inputs are portalled into the `.gap` spans the importer emitted rather
 * than produced by splitting the HTML. Splitting would cut through table cells
 * and list items — a gap inside a `<td>` would tear the table apart — whereas a
 * portal leaves the structure exactly as the instructor wrote it.
 */
export function GapBody({
  html,
  renderGap,
}: {
  html: string;
  renderGap: (n: number) => ReactNode;
}) {
  const host = useRef<HTMLDivElement>(null);
  const [slots, setSlots] = useState<{ n: number; el: HTMLElement }[]>([]);

  useEffect(() => {
    const nodes = host.current?.querySelectorAll<HTMLElement>('.gap');
    setSlots(
      [...(nodes ?? [])].map((el) => {
        el.textContent = '';
        return { n: Number(el.dataset.gap), el };
      }),
    );
  }, [html]);

  return (
    <>
      <div className="qbody" data-markable ref={host} dangerouslySetInnerHTML={{ __html: html }} />
      {slots.map(({ n, el }) => createPortal(renderGap(n), el, `gap-${n}`))}
    </>
  );
}

/** A numbered blank in a body of notes. */
export function GapInput({
  group,
  number,
  answer,
  disabled,
  onChange,
  onFocus,
  numberInside = false,
}: AnswerProps & { group: SittingGroup; numberInside?: boolean }) {
  const label = `Question ${number}`;

  if (group.inputMode === 'BANK' && group.optionsBank) {
    return (
      <span className="xgap">
        <span className="xnum">{number}</span>
        <select
          className="xinput"
          aria-label={label}
          disabled={disabled}
          value={answer?.value ?? ''}
          onFocus={onFocus}
          onChange={(e) => onChange({ value: e.target.value })}
        >
          <option value="">—</option>
          {group.optionsBank.map((option) => (
            <option key={option.letter} value={option.letter}>
              {option.letter}
            </option>
          ))}
        </select>
      </span>
    );
  }

  /**
   * On a Listening paper the number sits inside the empty box, as the real
   * screen prints it, and gives way to whatever the candidate types. The strip
   * along the bottom is what tells them which question they are on after that
   * — which is also how the real one works.
   */
  return (
    <span className="xgap">
      {!numberInside && <span className="xnum">{number}</span>}
      <input
        className={`xinput${numberInside ? ' xinput--numbered' : ''}`}
        type="text"
        autoComplete="off"
        autoCorrect="off"
        spellCheck={false}
        aria-label={label}
        placeholder={numberInside ? String(number) : undefined}
        disabled={disabled}
        value={answer?.value ?? ''}
        onFocus={onFocus}
        onChange={(e) => onChange({ value: e.target.value })}
      />
    </span>
  );
}

/**
 * Multiple choice, and True/False/Not Given.
 *
 * Each row is a lettered badge, a radio and the option's text, which is how the
 * real screen prints both — including True/False/Not Given, where the labels
 * are answers A, B and C even though the answer recorded is the word.
 */
export interface ChoiceItem {
  /** The badge beside the radio. */
  letter: string;
  text: string;
  /** What is recorded when it is chosen. Defaults to the letter. */
  value?: string;
}

export function ChoiceControl({
  group,
  number,
  answer,
  disabled,
  onChange,
  onFocus,
  options,
  limit,
}: AnswerProps & {
  group: SittingGroup;
  options: ChoiceItem[];
  /** "Choose THREE letters": how many may be ticked. No limit when absent. */
  limit?: number;
}) {
  const selected = new Set(
    (answer?.value ?? '')
      .split(',')
      .map((s) => s.trim().toUpperCase())
      .filter(Boolean),
  );
  const valueOf = (option: ChoiceItem) => option.value ?? option.letter;

  if (group.inputMode === 'CHOICE_MANY') {
    // "Choose TWO letters" — order is not part of the answer, so the letters are
    // kept sorted and the scorer compares them as a set.
    // As on the real screen, the boxes left stop taking a tick once the
    // question has as many as it asks for; untick one to change your mind.
    const full = limit != null && selected.size >= limit;
    const toggle = (letter: string) => {
      const next = new Set(selected);
      if (next.has(letter)) next.delete(letter);
      else if (full) return;
      else next.add(letter);
      onChange({ value: [...next].sort().join(', ') });
    };
    return (
      <ul className="xoptions">
        {options.map((option) => (
          <li key={option.letter}>
            {/* The chosen row is marked on the label, not left to :has(), so
                the band paints the same on an old machine in a test centre. */}
            <label className="xoption" data-chosen={selected.has(valueOf(option).toUpperCase()) || undefined}>
              <span className="xoption__letter">{option.letter}</span>
              <input
                type="checkbox"
                disabled={disabled || (full && !selected.has(valueOf(option).toUpperCase()))}
                checked={selected.has(valueOf(option).toUpperCase())}
                onFocus={onFocus}
                onChange={() => toggle(valueOf(option))}
              />
              <span className="xoption__text" data-markable>{option.text}</span>
            </label>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <ul className="xoptions">
      {options.map((option) => (
        <li key={option.letter}>
          <label className="xoption" data-chosen={selected.has(valueOf(option).toUpperCase()) || undefined}>
            <span className="xoption__letter">{option.letter}</span>
            <input
              type="radio"
              name={`q${number}`}
              disabled={disabled}
              checked={selected.has(valueOf(option).toUpperCase())}
              onFocus={onFocus}
              onChange={() => onChange({ value: valueOf(option) })}
            />
            <span className="xoption__text" data-markable>{option.text}</span>
          </label>
        </li>
      ))}
    </ul>
  );
}

/**
 * The legend printed above a True/False/Not Given group, in the exam's own
 * words. It is fixed wording, so it is stated here rather than depending on a
 * paper having typed it out — a paper that omits it would otherwise leave the
 * candidate guessing what "Not Given" means.
 */
export function FixedLegend({ type }: { type: string }) {
  const rows =
    type === 'YNNG'
      ? [
          ['YES', 'if the statement agrees with the views of the writer'],
          ['NO', 'if the statement contradicts the views of the writer'],
          ['NOT GIVEN', 'if it is impossible to say what the writer thinks about this'],
        ]
      : [
          ['TRUE', 'if the statement agrees with the information'],
          ['FALSE', 'if the statement contradicts the information'],
          ['NOT GIVEN', 'if there is no information on this'],
        ];

  return (
    <dl className="xlegend">
      {rows.map(([label, meaning]) => (
        <div key={label}>
          <dt data-markable>{label}</dt>
          <dd data-markable>{meaning}</dd>
        </div>
      ))}
    </dl>
  );
}

/** A dropdown of bank letters, for matching and for labelling artwork. */
export function BankSelect({
  group,
  number,
  answer,
  disabled,
  onChange,
  onFocus,
}: AnswerProps & { group: SittingGroup }) {
  return (
    <select
      className="xinput"
      aria-label={`Question ${number}`}
      disabled={disabled}
      value={answer?.value ?? ''}
      onFocus={onFocus}
      onChange={(e) => onChange({ value: e.target.value })}
    >
      <option value="">—</option>
      {(group.optionsBank ?? []).map((option) => (
        <option key={option.letter} value={option.letter}>
          {option.letter}
        </option>
      ))}
    </select>
  );
}

/** A short written answer against its own prompt. */
export function TextControl({ number, answer, disabled, onChange, onFocus }: AnswerProps) {
  return (
    <input
      className="xinput"
      type="text"
      autoComplete="off"
      autoCorrect="off"
      spellCheck={false}
      aria-label={`Question ${number}`}
      disabled={disabled}
      value={answer?.value ?? ''}
      onFocus={onFocus}
      onChange={(e) => onChange({ value: e.target.value })}
    />
  );
}
